import { getChecklistVisibilityStorageKey } from '#app/components/ProfileCompletionChecklist/constants.js';
import { useSidebar } from '#app/contexts/SidebarContext/useSidebar.js';
import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { sessionStorage } from '#app/services/storage.js';
import { theme } from '#app/theme/theme.js';
import { getRoute } from '@contra/routes';
import { useEffect } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

const routesToHideOn = [getRoute('/messages'), getRoute('/get-started')];

export const useShouldHideIntercom = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isMenuExpanded: isSidenavMenuExpanded } = useSidebar();

  const shouldHide =
    routesToHideOn.some((route) => location.pathname.startsWith(route)) ||
    (isMobile && !isSidenavMenuExpanded);

  return { shouldHide };
};

const IntercomChat = () => {
  const appConfiguration = useAppConfiguration();
  const currentUser = useCurrentUser();
  const { boot, shutdown } = useIntercom();
  const isMobile = useIsMobile();
  const CHECKLIST_DISMISSED_STORAGE_KEY = getChecklistVisibilityStorageKey(
    currentUser?.id,
  );
  const CHECKLIST_DISMISSED_TEST_KEY = getChecklistVisibilityStorageKey('TEST');
  const profileCompletionChecklistDismissed =
    sessionStorage.getItem(CHECKLIST_DISMISSED_STORAGE_KEY) === 'true' ||
    sessionStorage.getItem(CHECKLIST_DISMISSED_TEST_KEY) === 'true';
  const hasDismissedFeaturesUnlockedExperience = useHasUserInterfaceFlags(
    'HIDE_NEW_FEATURES_UNLOCKED_EXPERIENCE',
  );
  const { shouldHide } = useShouldHideIntercom();

  useEffect(() => {
    let isBooted = false;

    const isShowingCompleteProfileChecklist =
      !currentUser?.userGroups?.includes('CONTRACTOR_PROFILE_COMPLETE') &&
      !profileCompletionChecklistDismissed;

    if (
      !appConfiguration.playwrightTest &&
      currentUser?.intercomUserHash &&
      !shouldHide
    ) {
      boot({
        actionColor: theme.colorsV2.brandPurple,
        alignment:
          isMobile &&
          (isShowingCompleteProfileChecklist ||
            !hasDismissedFeaturesUnlockedExperience)
            ? 'right'
            : undefined,
        backgroundColor: theme.colorsV2.brandPurple,
        horizontalPadding: isMobile ? 16 : 24,
        userHash: currentUser.intercomUserHash,
        userId: currentUser.analyticsUserId,
        verticalPadding: isMobile
          ? 24
          : isShowingCompleteProfileChecklist ||
              !hasDismissedFeaturesUnlockedExperience
            ? 108
            : 32,
      });
      isBooted = true;
    }

    return () => {
      if (isBooted) {
        shutdown();
      }
    };
  }, [
    appConfiguration.playwrightTest,
    boot,
    currentUser?.analyticsUserId,
    currentUser?.intercomUserHash,
    currentUser?.selectedUserType,
    currentUser?.userGroups,
    hasDismissedFeaturesUnlockedExperience,
    isMobile,
    profileCompletionChecklistDismissed,
    shutdown,
    shouldHide,
  ]);

  return null;
};

export const Intercom = () => {
  const appConfiguration = useAppConfiguration();
  return (
    <IntercomProvider
      appId={appConfiguration.intercomAppId}
      autoBoot={false}
    >
      <IntercomChat />
    </IntercomProvider>
  );
};
